import type { RequestFn } from '../http';
import type {
  AdminTrip,
  ManualTrip,
  ManualTripType,
  Submission,
  TripCorrectionRequest,
} from '../types';

export function createTripsApi(request: RequestFn) {
  return {
    async createCorrectionRequest(data: {
      submissionId: string;
      requestedTravelKmOut?: number;
      requestedTravelMinutesOut?: number;
      requestedTravelKmBack?: number;
      requestedTravelMinutesBack?: number;
      reason: string;
    }) {
      return request<{ correctionRequest: TripCorrectionRequest }>('/trips/corrections', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getMyCorrectionRequests() {
      return request<{ correctionRequests: TripCorrectionRequest[] }>('/trips/corrections');
    },

    async cancelCorrectionRequest(id: string) {
      return request<{ success: boolean }>(`/trips/corrections/${id}`, {
        method: 'DELETE',
      });
    },

    async getAdminTrips() {
      return request<{ trips: AdminTrip[] }>('/admin/trips');
    },

    async getAdminCorrectionRequests(status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL') {
      const query = status ? `?status=${status}` : '';
      return request<{ correctionRequests: TripCorrectionRequest[] }>(
        `/admin/trips/corrections${query}`
      );
    },

    async reviewCorrectionRequest(
      id: string,
      data: { status: 'APPROVED' | 'REJECTED'; adminNote?: string }
    ) {
      return request<{ correctionRequest: TripCorrectionRequest }>(
        `/admin/trips/corrections/${id}/review`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    async updateTrip(
      submissionId: string,
      data: {
        travelKmOut?: number;
        travelMinutesOut?: number;
        travelKmBack?: number;
        travelMinutesBack?: number;
      }
    ) {
      return request<{ submission: Submission }>(`/admin/trips/${submissionId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    async createManualTrip(data: {
      tripDate: string;
      tripType: ManualTripType;
      description: string;
      departureLocation: string;
      destinationLocation: string;
      distanceKm: number;
      durationMinutes: number;
      isRoundTrip: boolean;
    }) {
      return request<{ manualTrip: ManualTrip }>('/trips/manual', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getMyManualTrips() {
      return request<{ manualTrips: ManualTrip[] }>('/trips/manual');
    },

    async deleteManualTrip(id: string) {
      return request<{ success: boolean }>(`/trips/manual/${id}`, {
        method: 'DELETE',
      });
    },

    async getAdminManualTrips(status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL') {
      const query = status ? `?status=${status}` : '';
      return request<{ manualTrips: ManualTrip[] }>(`/admin/trips/manual${query}`);
    },

    async reviewManualTrip(
      id: string,
      data: {
        status: 'APPROVED' | 'REJECTED';
        adminNote?: string;
        /** Umrechnung auf einen anderen Endpunkt; Original bleibt am Datensatz erhalten. */
        adjustment?: {
          departureLocation: string;
          destinationLocation: string;
          distanceKm: number;
          durationMinutes: number;
          isRoundTrip?: boolean;
          note?: string;
        };
      }
    ) {
      return request<{ manualTrip: ManualTrip }>(`/admin/trips/manual/${id}/review`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /** Vorschau: Fahrt auf einen anderen Start-/Endpunkt umrechnen (speichert nichts). */
    async recalculateManualTrip(
      id: string,
      data: { departureLocation: string; destinationLocation: string }
    ) {
      return request<{
        departureLocation: string;
        destinationLocation: string;
        distanceKm: number;
        durationMinutes: number;
        original: {
          departureLocation: string;
          destinationLocation: string;
          distanceKm: number;
          durationMinutes: number;
        };
      }>(`/admin/trips/manual/${id}/recalculate`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  };
}
